import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { getCountryDefaultRate, getVatRatesForCountry } from "@/utils/vatRates";
import UpgradeRequiredView from "@/components/UpgradeRequiredView";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Plus, Trash2, FileDown, Send, Edit, ArrowRightLeft, Link2, FileSignature, BellRing, ExternalLink,
  Paperclip, Loader2, Download, CheckCircle2, XCircle, ChevronUp, ChevronDown,
} from "lucide-react";

const EUR = (v, currency = "EUR") =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency }).format(v || 0);
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("de-DE") : "—");
const toInputDate = (d) => (d ? new Date(d).toISOString().slice(0, 10) : "");

const EU_COUNTRIES = new Set(["AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE"]);

const STATUS_META = {
  draft: { label: "Entwurf", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  sent: { label: "Versendet", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  accepted: { label: "Angenommen", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  declined: { label: "Abgelehnt", cls: "bg-red-100 text-red-700 border-red-200" },
  expired: { label: "Abgelaufen", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  converted: { label: "In Rechnung", cls: "bg-violet-100 text-violet-700 border-violet-200" },
};

const SCENARIO_NOTE = {
  reverse_charge: "Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge). Keine USt ausgewiesen.",
  intra_community: "Steuerfreie innergemeinschaftliche Lieferung. Keine USt ausgewiesen.",
  export: "Steuerfreie Ausfuhrlieferung (Drittland). Keine USt ausgewiesen.",
  exempt: "Steuerfreier Umsatz. Keine USt ausgewiesen.",
};
const COUNTRY_OPTIONS = [
  { code: "DE", label: "Deutschland" }, { code: "AT", label: "Österreich" }, { code: "CH", label: "Schweiz" },
  { code: "FR", label: "Frankreich" }, { code: "IT", label: "Italien" }, { code: "NL", label: "Niederlande" },
  { code: "ES", label: "Spanien" }, { code: "PL", label: "Polen" }, { code: "US", label: "USA" }, { code: "GB", label: "Großbritannien" },
];

function isPaid(user) {
  if (!user) return false;
  if (user.has_lifetime_access) return true;
  return user.plan && user.plan !== "free";
}

const EMPTY_INLINE = { name: "", email: "", vat_id: "", street: "", postal_code: "", city: "", country_code: "DE" };

export default function Quotes() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userCountry = (user?.country || "DE").toUpperCase();

  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [standardArticles, setStandardArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState({ open: false, id: null });
  const [emailDialog, setEmailDialog] = useState({ open: false, quote: null, recipient: "", sending: false });
  const [useInlineCustomer, setUseInlineCustomer] = useState(false);
  const [inlineCustomer, setInlineCustomer] = useState({ ...EMPTY_INLINE });
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [promoteDialog, setPromoteDialog] = useState({ open: false, customerId: null, customerName: "" });
  const [reminderConfirm, setReminderConfirm] = useState({ open: false, quote: null, sending: false });
  const [declineDialog, setDeclineDialog] = useState({ open: false, quote: null, reason: "", saving: false });
  const [scenarioManual, setScenarioManual] = useState(false);
  const [reasonOpen, setReasonOpen] = useState({});

  const emptyItem = () => ({ description: "", quantity: 1, unit_price: 0, tax_rate: getCountryDefaultRate(userCountry, 0) });

  const today = new Date();
  const defaultValid = new Date(today.getTime() + 30 * 86400000);
  const [form, setForm] = useState(null);

  const baseForm = () => ({
    customer_id: "",
    project_id: "",
    quote_date: toInputDate(today),
    valid_until: toInputDate(defaultValid),
    notes: "",
    is_non_binding: true,
    reminder_recipients: "self",
    tax_scenario: "standard",
    items: [emptyItem()],
    attachments: [],
  });

  useEffect(() => {
    if (isPaid(user)) {
      Promise.all([fetchQuotes(), fetchCustomers(), fetchProjects(), fetchStandardArticles()]).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Deep-Link aus dem Landing-CTA „Angebote testen": ?new=1 öffnet direkt den Erstellen-Dialog
  useEffect(() => {
    if (searchParams.get("new") === "1" && isPaid(user)) {
      openCreate();
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, [user]);

  const fetchQuotes = async () => {
    try { const res = await api.get("/quotes"); setQuotes(res.data || []); } catch (e) { console.error(e); }
  };
  const fetchCustomers = async () => {
    try { const res = await api.get("/customers"); setCustomers((res.data || []).filter((c) => !c.is_temporary)); } catch (e) {}
  };
  const fetchProjects = async () => {
    try { const res = await api.get("/projects"); setProjects(res.data || []); } catch (e) {}
  };
  const fetchStandardArticles = async () => {
    try { const res = await api.get("/standard-articles"); setStandardArticles(res.data?.articles || []); } catch (e) {}
  };

  const SERVICE_KW = ["beratung", "consulting", "entwicklung", "service", "leistung", "stunden", "honorar", "freelance", "support", "training", "coaching", "lizenz", "subscription", "software", "saas"];

  // Steuerart-Auto-Erkennung (gespiegelt vom Rechnungsgenerator):
  // Cross-Border (Kundenland ≠ eigenes Land) → RC / IGL / Export je nach EU + USt-IdNr + Service/Ware.
  const scenarioFor = (customer, items) => {
    const custC = (customer?.country_code || "").toUpperCase();
    if (!custC || custC === userCountry) return "standard";
    const isEU = EU_COUNTRIES.has(custC);
    const descs = (items || []).map((it) => (it.description || "").toLowerCase()).join(" ");
    const looksService = SERVICE_KW.some((k) => descs.includes(k));
    if (isEU && customer?.vat_id) return looksService ? "reverse_charge" : "intra_community";
    if (!isEU) return looksService ? "reverse_charge" : "export";
    return "standard";
  };

  // Aktuell ausgewählter Kunde (inline oder bestehend)
  const selectedCustomerObj = () => useInlineCustomer
    ? { ...inlineCustomer, country_code: (inlineCustomer.country_code || "").toUpperCase() }
    : customers.find((c) => c.id === form?.customer_id);

  // Auto-Erkennung der Steuerart live bei Änderung von Kunde/Positionen — außer der User wählt manuell.
  useEffect(() => {
    if (!dialogOpen || !form || user?.is_kleinunternehmer || scenarioManual) return;
    const auto = scenarioFor(selectedCustomerObj(), form.items);
    if (auto !== form.tax_scenario) setForm((f) => ({ ...f, tax_scenario: auto }));
  }, [dialogOpen, scenarioManual, form?.customer_id, form?.items, useInlineCustomer, inlineCustomer.country_code, inlineCustomer.vat_id]);

  const openCreate = () => {
    setEditing(null);
    setUseInlineCustomer(false);
    setScenarioManual(false);
    setInlineCustomer({ ...EMPTY_INLINE, country_code: userCountry });
    setForm(baseForm());
    setDialogOpen(true);
  };

  const openEdit = (q) => {
    setEditing(q);
    setUseInlineCustomer(false);
    setScenarioManual(true); // gespeicherte Steuerart beibehalten (nicht automatisch überschreiben)
    setForm({
      customer_id: q.customer_id || "",
      project_id: q.project_id || "",
      quote_date: toInputDate(q.quote_date),
      valid_until: toInputDate(q.valid_until),
      notes: q.notes || "",
      is_non_binding: q.is_non_binding !== false,
      reminder_recipients: q.reminder_recipients || "self",
      tax_scenario: q.tax_scenario || "standard",
      items: (q.items || []).map((i) => ({ description: i.description, quantity: i.quantity, unit_price: i.unit_price, tax_rate: i.tax_rate })),
      attachments: q.attachments || [],
    });
    setDialogOpen(true);
  };

  const onCustomerChange = (id) => {
    setScenarioManual(false);
    setForm((f) => ({ ...f, customer_id: id }));
  };

  const zeroVat = useMemo(() => {
    return !!user?.is_kleinunternehmer || (form && form.tax_scenario !== "standard");
  }, [user, form]);

  const setItem = (idx, field, value) => {
    setForm((f) => { const items = [...f.items]; items[idx] = { ...items[idx], [field]: value }; return { ...f, items }; });
  };
  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const MAX_ATT = 10;
  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (files.length === 0) return;
    if ((form.attachments?.length || 0) + files.length > MAX_ATT) {
      return toast.error(`Maximal ${MAX_ATT} Anhänge pro Angebot`);
    }
    setUploadingAtt(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await api.post("/quotes/upload-attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => ({ ...f, attachments: [...(f.attachments || []), res.data] }));
      }
      toast.success(files.length > 1 ? `${files.length} Anhänge hochgeladen` : "Anhang hochgeladen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploadingAtt(false);
    }
  };

  const removeAttachment = async (att) => {
    // Bereits gespeicherter Anhang eines bestehenden Angebots → serverseitig löschen
    if (editing && (editing.attachments || []).some((a) => a.id === att.id)) {
      try {
        await api.delete(`/quotes/${editing.id}/attachments/${att.id}`);
      } catch (err) {
        return toast.error(err.response?.data?.detail || "Anhang konnte nicht gelöscht werden");
      }
    }
    setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((a) => a.id !== att.id) }));
  };

  const downloadAttachment = async (quoteId, att) => {
    try {
      const res = await api.get(`/quotes/${quoteId}/attachments/${att.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: att.content_type || "application/octet-stream" }));
      window.open(url, "_blank");
    } catch (err) { toast.error("Anhang konnte nicht geöffnet werden"); }
  };

  const fmtSize = (b) => (!b ? "" : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

  const applyStandardArticle = (idx, articleId) => {
    if (!articleId) return;
    const a = standardArticles.find((x) => x.id === articleId);
    if (!a) return;
    setForm((f) => {
      const items = [...f.items];
      items[idx] = {
        ...items[idx],
        description: a.description ? `${a.name} — ${a.description}` : a.name,
        quantity: a.default_quantity ?? 1,
        unit_price: a.unit_price ?? 0,
        tax_rate: zeroVat ? 0 : (a.vat_rate ?? items[idx].tax_rate),
      };
      return { ...f, items };
    });
  };

  const computed = useMemo(() => {
    if (!form) return { subtotal: 0, tax: 0, total: 0 };
    let subtotal = 0, tax = 0;
    form.items.forEach((i) => {
      const line = (Number(i.quantity) || 0) * (Number(i.unit_price) || 0);
      subtotal += line;
      if (!zeroVat) tax += line * (Number(i.tax_rate) || 0) / 100;
    });
    return { subtotal, tax, total: subtotal + tax };
  }, [form, zeroVat]);

  const buildItemsPayload = () =>
    form.items.filter((i) => i.description.trim()).map((i) => {
      const total = (Number(i.quantity) || 0) * (Number(i.unit_price) || 0);
      return {
        description: i.description, quantity: Number(i.quantity) || 0,
        unit_price: Number(i.unit_price) || 0,
        tax_rate: zeroVat ? 0 : (Number(i.tax_rate) || 0), total,
      };
    });

  const handleSave = async () => {
    if (!editing) {
      if (useInlineCustomer) {
        if (!inlineCustomer.name.trim()) return toast.error("Bitte Namen des neuen Kunden eingeben");
      } else if (!form.customer_id) {
        return toast.error("Bitte Kunde auswählen oder neuen Kunden anlegen");
      }
    }
    const items = buildItemsPayload();
    if (items.length === 0) return toast.error("Bitte mindestens eine Position mit Beschreibung");
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/quotes/${editing.id}`, {
          quote_date: new Date(form.quote_date).toISOString(),
          valid_until: new Date(form.valid_until).toISOString(),
          notes: form.notes || null,
          is_non_binding: form.is_non_binding,
          reminder_recipients: form.reminder_recipients,
          tax_scenario: form.tax_scenario,
          items,
          attachments: form.attachments || [],
        });
        toast.success("Angebot aktualisiert");
      } else {
        const payload = {
          project_id: form.project_id || null,
          quote_date: new Date(form.quote_date).toISOString(),
          valid_until: new Date(form.valid_until).toISOString(),
          notes: form.notes || null,
          is_non_binding: form.is_non_binding,
          reminder_recipients: form.reminder_recipients,
          tax_scenario: form.tax_scenario,
          items,
          attachments: form.attachments || [],
        };
        if (useInlineCustomer) payload.inline_customer = { ...inlineCustomer };
        else payload.customer_id = form.customer_id;
        const res = await api.post("/quotes", payload);
        toast.success("Angebot erstellt");
        // Inline-Kunde → fragen, ob dauerhaft in Kunden-DB übernehmen (wie im Rechnungsgenerator)
        if (useInlineCustomer && res.data?.customer_id) {
          setPromoteDialog({ open: true, customerId: res.data.customer_id, customerName: inlineCustomer.name });
        }
      }
      setDialogOpen(false);
      fetchQuotes();
      fetchCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const promoteCustomer = async () => {
    try {
      await api.post(`/customers/${promoteDialog.customerId}/promote`);
      toast.success(`„${promoteDialog.customerName}" zur Kunden-DB hinzugefügt`);
      fetchCustomers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Konnte Kunde nicht übernehmen.");
    } finally {
      setPromoteDialog({ open: false, customerId: null, customerName: "" });
    }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/quotes/${q.id}/pdf`, { params: { base_url: window.location.origin }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error("PDF konnte nicht erstellt werden"); }
  };

  const openEmail = (q) => setEmailDialog({ open: true, quote: q, recipient: q.customer_email || "", sending: false });
  const sendEmail = async () => {
    const { quote, recipient } = emailDialog;
    if (!recipient.trim()) return toast.error("Bitte Empfänger-E-Mail eingeben");
    setEmailDialog((d) => ({ ...d, sending: true }));
    try {
      const res = await api.post("/quotes/send-email", { quote_id: quote.id, recipient_email: recipient, base_url: window.location.origin });
      toast.success(`Angebot versendet (${res.data.sender_used})`);
      setEmailDialog({ open: false, quote: null, recipient: "", sending: false });
      fetchQuotes();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Versand fehlgeschlagen");
      setEmailDialog((d) => ({ ...d, sending: false }));
    }
  };

  const sendReminder = async () => {
    const q = reminderConfirm.quote;
    if (!q) return;
    setReminderConfirm((d) => ({ ...d, sending: true }));
    try {
      await api.post(`/quotes/${q.id}/send-reminder`, { base_url: window.location.origin });
      toast.success("Erinnerung inkl. Angebot & Anhang an den Kunden gesendet");
      setReminderConfirm({ open: false, quote: null, sending: false });
      fetchQuotes();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Erinnerung fehlgeschlagen");
      setReminderConfirm((d) => ({ ...d, sending: false }));
    }
  };

  const markStatus = async (q, status, reason = null) => {
    try {
      await api.post(`/quotes/${q.id}/mark`, { status, reason });
      toast.success(status === "accepted" ? "Angebot als angenommen markiert" : "Angebot als abgelehnt markiert");
      fetchQuotes();
    } catch (e) { toast.error(e.response?.data?.detail || "Status konnte nicht geändert werden"); }
  };

  const submitDecline = async () => {
    const q = declineDialog.quote;
    if (!q) return;
    setDeclineDialog((d) => ({ ...d, saving: true }));
    await markStatus(q, "declined", declineDialog.reason.trim() || null);
    setDeclineDialog({ open: false, quote: null, reason: "", saving: false });
  };

  const convert = async (q) => {
    try {
      const res = await api.post(`/quotes/${q.id}/convert`);
      toast.success(`Als Rechnung ${res.data.invoice_number} übernommen`);
      fetchQuotes();
    } catch (e) { toast.error(e.response?.data?.detail || "Übernahme fehlgeschlagen"); }
  };

  const copyLink = async (q) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/angebot/${q.public_token}`);
      toast.success("Annahme-Link kopiert");
    } catch (e) { toast.error("Link konnte nicht kopiert werden"); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/quotes/${confirmDel.id}`);
      toast.success("Angebot gelöscht");
      setConfirmDel({ open: false, id: null });
      fetchQuotes();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
      setConfirmDel({ open: false, id: null });
    }
  };

  if (!isPaid(user)) {
    return (
      <UpgradeRequiredView
        source="quotes" icon={FileSignature} title="Angebote & Kostenvoranschläge"
        subtitle="Professionelle Angebote erstellen, versenden und mit einem Klick in Rechnungen übernehmen — ab Basic verfügbar."
        features={["Angebote als PDF mit deinem Logo", "Versand per E-Mail mit Online-Annahme-Link", "Status-Tracking: Versendet → Angenommen", "1-Klick-Übernahme als Rechnung"]}
      />
    );
  }

  const vatRates = getVatRatesForCountry(userCountry);

  return (
    <div className="space-y-8" data-testid="quotes-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: "Manrope, sans-serif" }}>Angebote</h1>
          <p className="text-lg text-gray-600">Erstelle Angebote und übernimm sie nach Annahme als Rechnung.</p>
        </div>
        <Button data-testid="add-quote-button" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />Neues Angebot</Button>
      </div>

      {loading ? (
        <Card><CardContent className="py-12 text-center text-gray-500">Lädt…</CardContent></Card>
      ) : quotes.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-gray-500" data-testid="quotes-empty">
          <FileSignature className="h-10 w-10 mx-auto mb-3 text-slate-300" />Noch keine Angebote. Erstelle dein erstes Angebot.
        </CardContent></Card>
      ) : (
        <div className="space-y-3" data-testid="quotes-list">
          {quotes.map((q) => {
            const st = STATUS_META[q.status] || STATUS_META.draft;
            const isClosed = ["converted", "accepted", "declined"].includes(q.status);
            const canEdit = !isClosed;
            const canConvert = !q.converted_to_invoice_id && ["draft", "sent", "accepted"].includes(q.status);
            const alreadySent = !!q.email_sent_at || ["sent", "expired"].includes(q.status);
            const canSendOrRemind = !isClosed;
            const canShowLink = !isClosed;
            const canMark = ["draft", "sent", "expired"].includes(q.status);
            const hasReason = q.status === "declined" && q.decline_reason;
            return (
              <Card key={q.id} data-testid={`quote-card-${q.id}`} className="card-hover">
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-semibold text-slate-900">{q.quote_number}</span>
                        <Badge variant="outline" className={st.cls} data-testid={`quote-status-${q.id}`}>{st.label}</Badge>
                        {q.converted_invoice_number && (
                          <button onClick={() => navigate("/invoices")} data-testid={`quote-invoice-link-${q.id}`}
                            className="text-xs text-violet-600 hover:underline inline-flex items-center gap-1">
                            → {q.converted_invoice_number} <ExternalLink className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <div className="text-sm text-gray-600 mt-1 truncate">{q.customer_name} · gültig bis {fmtDate(q.valid_until)}</div>
                      {(q.attachments || []).length > 0 && (
                        <button type="button" data-testid={`quote-attachment-count-${q.id}`}
                          onClick={() => { if (q.attachments.length === 1) { downloadAttachment(q.id, q.attachments[0]); } else { openEdit(q); } }}
                          title={q.attachments.length === 1 ? "Anhang herunterladen" : "Anhänge im Dialog öffnen"}
                          className="text-xs text-blue-600 hover:underline mt-1 inline-flex items-center gap-1 cursor-pointer">
                          <Paperclip className="h-3 w-3" /> {q.attachments.length} {q.attachments.length === 1 ? "Anhang" : "Anhänge"}
                        </button>
                      )}
                      {hasReason && (
                        <div className="mt-1">
                          <button type="button" data-testid={`quote-reason-toggle-${q.id}`}
                            onClick={() => setReasonOpen((s) => ({ ...s, [q.id]: !s[q.id] }))}
                            className="text-xs text-red-600 hover:underline inline-flex items-center gap-1">
                            {reasonOpen[q.id] ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                            Ablehnungsgrund {reasonOpen[q.id] ? "ausblenden" : "anzeigen"}
                          </button>
                          {reasonOpen[q.id] && (
                            <div data-testid={`quote-reason-text-${q.id}`} className="mt-1.5 text-sm text-slate-700 bg-red-50 border border-red-100 rounded-md px-3 py-2 max-w-xl">
                              {q.decline_reason}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold text-slate-900">{EUR(q.total, q.currency)}</div>
                      <div className="text-xs text-gray-500">{fmtDate(q.quote_date)}</div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                    <Button size="sm" variant="outline" data-testid={`quote-pdf-${q.id}`} onClick={() => downloadPdf(q)}><FileDown className="h-4 w-4 mr-1" /> PDF</Button>
                    {canSendOrRemind && (alreadySent ? (
                      <Button size="sm" variant="outline" data-testid={`quote-remind-${q.id}`} disabled={!q.customer_email}
                        title={q.customer_email ? "" : "Kunde hat keine E-Mail hinterlegt"}
                        onClick={() => setReminderConfirm({ open: true, quote: q, sending: false })}>
                        <BellRing className="h-4 w-4 mr-1" /> Erinnerung
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" data-testid={`quote-send-${q.id}`} onClick={() => openEmail(q)}><Send className="h-4 w-4 mr-1" /> Senden</Button>
                    ))}
                    {canShowLink && (<Button size="sm" variant="outline" data-testid={`quote-copylink-${q.id}`} onClick={() => copyLink(q)}><Link2 className="h-4 w-4 mr-1" /> Link</Button>)}
                    {canEdit && (<Button size="sm" variant="outline" data-testid={`quote-edit-${q.id}`} onClick={() => openEdit(q)}><Edit className="h-4 w-4 mr-1" /> Bearbeiten</Button>)}
                    {canMark && (
                      <>
                        <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50" data-testid={`quote-mark-accepted-${q.id}`} onClick={() => markStatus(q, "accepted")}><CheckCircle2 className="h-4 w-4 mr-1" /> Angenommen</Button>
                        <Button size="sm" variant="outline" className="text-red-700 border-red-200 hover:bg-red-50" data-testid={`quote-mark-declined-${q.id}`} onClick={() => setDeclineDialog({ open: true, quote: q, reason: "", saving: false })}><XCircle className="h-4 w-4 mr-1" /> Abgelehnt</Button>
                      </>
                    )}
                    {canConvert && (<Button size="sm" data-testid={`quote-convert-${q.id}`} onClick={() => convert(q)}><ArrowRightLeft className="h-4 w-4 mr-1" /> Als Rechnung übernehmen</Button>)}
                    {q.status !== "converted" && (<Button size="sm" variant="outline" className="ml-auto" data-testid={`quote-delete-${q.id}`} onClick={() => setConfirmDel({ open: true, id: q.id })}><Trash2 className="h-4 w-4 text-red-600" /></Button>)}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Angebot ${editing.quote_number} bearbeiten` : "Neues Angebot"}</DialogTitle></DialogHeader>
          {form && (
          <div className="space-y-5">
            {/* Kunde + Projekt */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <div className="flex items-center justify-between">
                  <Label>Kunde *</Label>
                  {!editing && (
                    <button type="button" data-testid="quote-toggle-inline-customer"
                      className="text-xs text-emerald-600 hover:underline"
                      onClick={() => { setScenarioManual(false); setUseInlineCustomer((v) => !v); }}>
                      {useInlineCustomer ? "Bestehenden wählen" : "+ Neuer Kunde"}
                    </button>
                  )}
                </div>
                {!useInlineCustomer ? (
                  <select data-testid="quote-customer-select"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
                    value={form.customer_id} disabled={!!editing}
                    onChange={(e) => onCustomerChange(e.target.value)}>
                    <option value="">Bitte wählen…</option>
                    {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <Input data-testid="quote-inline-name" placeholder="Name / Firma *" value={inlineCustomer.name}
                    onChange={(e) => setInlineCustomer({ ...inlineCustomer, name: e.target.value })} />
                )}
              </div>
              <div>
                <Label>Projekt (optional)</Label>
                <select data-testid="quote-project-select"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm disabled:opacity-50"
                  value={form.project_id} disabled={!!editing}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value })}>
                  <option value="">—</option>
                  {projects.filter((p) => !form.customer_id || p.customer_id === form.customer_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>

            {/* Inline-Kunde Felder */}
            {useInlineCustomer && !editing && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="quote-inline-customer-fields">
                <Input data-testid="quote-inline-email" placeholder="E-Mail" value={inlineCustomer.email} onChange={(e) => setInlineCustomer({ ...inlineCustomer, email: e.target.value })} />
                <Input data-testid="quote-inline-vatid" placeholder="USt-IdNr. (optional)" value={inlineCustomer.vat_id} onChange={(e) => setInlineCustomer({ ...inlineCustomer, vat_id: e.target.value })} />
                <Input data-testid="quote-inline-street" placeholder="Straße & Nr." value={inlineCustomer.street} onChange={(e) => setInlineCustomer({ ...inlineCustomer, street: e.target.value })} />
                <div className="grid grid-cols-3 gap-2">
                  <Input data-testid="quote-inline-plz" placeholder="PLZ" value={inlineCustomer.postal_code} onChange={(e) => setInlineCustomer({ ...inlineCustomer, postal_code: e.target.value })} />
                  <Input className="col-span-2" data-testid="quote-inline-city" placeholder="Ort" value={inlineCustomer.city} onChange={(e) => setInlineCustomer({ ...inlineCustomer, city: e.target.value })} />
                </div>
                <div>
                  <input data-testid="quote-inline-country" list="quote-country-suggestions"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm uppercase"
                    placeholder="Land (z.B. DE, AT, FR…)" maxLength={2}
                    value={inlineCustomer.country_code}
                    onChange={(e) => { const cc = e.target.value.toUpperCase().slice(0, 2); setScenarioManual(false); setInlineCustomer({ ...inlineCustomer, country_code: cc }); }} />
                  <datalist id="quote-country-suggestions">
                    {COUNTRY_OPTIONS.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </datalist>
                  <span className="text-[11px] text-gray-400">Vorschläge wählen oder eigenen ISO-Code eingeben</span>
                </div>
              </div>
            )}

            {/* Daten */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Angebotsdatum</Label><Input type="date" data-testid="quote-date-input" value={form.quote_date} onChange={(e) => setForm({ ...form, quote_date: e.target.value })} /></div>
              <div><Label>Gültig bis</Label><Input type="date" data-testid="quote-validuntil-input" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
            </div>
            {user?.is_kleinunternehmer && (
              <p className="text-xs text-amber-600" data-testid="quote-ku-note">Kleinunternehmer (§19 UStG): keine USt ausgewiesen.</p>
            )}

            {/* Steuerart-Card (Cross-Border) — gespiegelt vom Rechnungsgenerator */}
            {(() => {
              if (user?.is_kleinunternehmer) return null;
              const sellerCountry = userCountry;
              const selectedCustomer = useInlineCustomer
                ? inlineCustomer
                : customers.find((c) => c.id === form.customer_id);
              const customerCountry = (selectedCustomer?.country_code || "").toUpperCase();
              const isCrossBorder = customerCountry && customerCountry !== sellerCountry;
              if (!isCrossBorder) return null;
              const autoDetected = scenarioFor(selectedCustomer, form.items);
              const scenario = form.tax_scenario && form.tax_scenario !== "standard" ? form.tax_scenario : autoDetected;
              const setScenario = (v) => { setScenarioManual(true); setForm((f) => ({ ...f, tax_scenario: v })); };
              const isAT = sellerCountry === "AT";
              const rcLabel = isAT ? "Reverse Charge (§ 19 Abs. 1a UStG 1994)" : "Reverse Charge (§ 13b UStG)";
              const iglLabel = isAT ? "Innergem. Lieferung (Art. 6 Abs. 1 UStG 1994)" : "Innergem. Lieferung (§ 4 Nr. 1b UStG)";
              const exportLabel = isAT ? "Drittland-Export (§ 7 UStG 1994)" : "Drittland-Export (§ 4 Nr. 1a UStG)";
              const opts = [
                { value: "standard", label: "Standard (mit USt)", desc: "Normaler USt-Ausweis – ungewöhnlich bei Cross-Border" },
                { value: "reverse_charge", label: rcLabel, desc: "B2B-Dienstleistung – Empfänger schuldet die USt" },
                { value: "intra_community", label: iglLabel, desc: "B2B-Warenlieferung in ein EU-Land (Kunde braucht USt-IdNr.)" },
                { value: "export", label: exportLabel, desc: "Warenlieferung in Drittland (USA, UK, CH …) – steuerfrei" },
              ];
              const customerLabel = selectedCustomer?.name || "Kunde";
              return (
                <div className="border-2 border-blue-300 bg-blue-50/60 rounded-xl p-4" data-testid="quote-tax-scheme-card">
                  <div className="mb-3">
                    <div className="text-sm font-semibold text-slate-800">Steuerart dieses Angebots</div>
                    <div className="text-[11px] text-slate-600 mt-0.5">
                      Cross-Border erkannt: <strong>{customerLabel}</strong> ({customerCountry}) ≠ dein Sitz ({sellerCountry}).
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {opts.map((opt) => (
                      <label key={opt.value}
                        className={`flex items-start gap-2 rounded-md border-2 p-2.5 cursor-pointer text-xs ${scenario === opt.value ? "border-blue-500 bg-white" : "border-slate-200 bg-white hover:border-slate-300"}`}
                        data-testid={`quote-tax-scheme-${opt.value}`}>
                        <input type="radio" checked={scenario === opt.value} onChange={() => setScenario(opt.value)} className="mt-0.5" />
                        <div>
                          <div className="font-medium text-slate-800">{opt.label}</div>
                          <div className="text-slate-500 mt-0.5 leading-snug">{opt.desc}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {SCENARIO_NOTE[scenario] && (
                    <p className="text-xs text-blue-700 mt-3 pt-3 border-t border-blue-200" data-testid="quote-scenario-note">{SCENARIO_NOTE[scenario]}</p>
                  )}
                </div>
              );
            })()}

            {/* Positionen */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Positionen</Label>
                <Button type="button" size="sm" variant="outline" data-testid="quote-add-item" onClick={addItem}><Plus className="h-4 w-4 mr-1" /> Position</Button>
              </div>
              <div className="space-y-3">
                {form.items.map((it, idx) => (
                  <div key={idx} className="rounded-lg border border-slate-200 p-3 space-y-2" data-testid={`quote-item-row-${idx}`}>
                    {standardArticles.length > 0 && (
                      <select data-testid={`quote-standard-article-${idx}`}
                        className="flex h-9 w-full rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-1 text-xs text-slate-600"
                        value="" onChange={(e) => { applyStandardArticle(idx, e.target.value); e.target.value = ""; }}>
                        <option value="">+ Standardartikel einfügen…</option>
                        {standardArticles.map((a) => <option key={a.id} value={a.id}>{a.name} ({EUR(a.unit_price)})</option>)}
                      </select>
                    )}
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-12 sm:col-span-5">
                        {idx === 0 && <Label className="text-xs text-gray-500">Beschreibung</Label>}
                        <Input data-testid={`quote-item-desc-${idx}`} value={it.description} placeholder="z.B. Beratung" onChange={(e) => setItem(idx, "description", e.target.value)} />
                      </div>
                      <div className="col-span-3 sm:col-span-2">
                        {idx === 0 && <Label className="text-xs text-gray-500">Menge</Label>}
                        <Input type="number" step="0.01" data-testid={`quote-item-qty-${idx}`} value={it.quantity} onChange={(e) => setItem(idx, "quantity", e.target.value)} />
                      </div>
                      <div className="col-span-4 sm:col-span-2">
                        {idx === 0 && <Label className="text-xs text-gray-500">Preis</Label>}
                        <Input type="number" step="0.01" data-testid={`quote-item-price-${idx}`} value={it.unit_price} onChange={(e) => setItem(idx, "unit_price", e.target.value)} />
                      </div>
                      <div className="col-span-3 sm:col-span-2">
                        {idx === 0 && <Label className="text-xs text-gray-500">USt</Label>}
                        {zeroVat ? (
                          <Input data-testid={`quote-item-tax-${idx}`} value="0%" disabled />
                        ) : (
                          <select data-testid={`quote-item-tax-${idx}`}
                            className="flex h-10 w-full rounded-md border border-input bg-background px-2 py-2 text-sm"
                            value={it.tax_rate} onChange={(e) => setItem(idx, "tax_rate", parseFloat(e.target.value))}>
                            {vatRates.map((r) => <option key={r.rate} value={r.rate}>{r.rate}%</option>)}
                          </select>
                        )}
                      </div>
                      <div className="col-span-2 sm:col-span-1 flex justify-end">
                        {form.items.length > 1 && (
                          <Button type="button" size="icon" variant="ghost" data-testid={`quote-item-remove-${idx}`} onClick={() => removeItem(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div><Label>Notizen (optional)</Label><Textarea data-testid="quote-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Hinweis für den Kunden…" /></div>

            {/* Anhänge (Leistungsbeschreibungen etc.) */}
            <div data-testid="quote-attachments-section">
              <div className="flex items-center justify-between mb-2">
                <Label className="flex items-center gap-1.5"><Paperclip className="h-4 w-4" /> Anhänge (Leistungsbeschreibungen)</Label>
                <label className={`text-xs inline-flex items-center gap-1 cursor-pointer ${uploadingAtt ? "text-gray-400 pointer-events-none" : "text-emerald-600 hover:underline"}`} data-testid="quote-attachment-upload-label">
                  {uploadingAtt ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                  {uploadingAtt ? "Lädt hoch…" : "Datei hinzufügen"}
                  <input type="file" multiple className="hidden" data-testid="quote-attachment-input"
                    accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx,.txt,.csv"
                    disabled={uploadingAtt} onChange={handleAttachmentUpload} />
                </label>
              </div>
              {(form.attachments || []).length === 0 ? (
                <p className="text-xs text-gray-400">Noch keine Anhänge. PDF, Bilder, Word/Excel (max. 25 MB).</p>
              ) : (
                <div className="space-y-1.5" data-testid="quote-attachments-list">
                  {(form.attachments || []).map((att) => {
                    const persisted = editing && (editing.attachments || []).some((a) => a.id === att.id);
                    return (
                    <div key={att.id} data-testid={`quote-attachment-${att.id}`} className="flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                      {persisted ? (
                        <button type="button" data-testid={`quote-attachment-open-${att.id}`} onClick={() => downloadAttachment(editing.id, att)}
                          className="flex items-center gap-2 min-w-0 text-left group" title="Anhang öffnen">
                          <Paperclip className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          <span className="text-sm text-blue-700 group-hover:underline truncate">{att.filename}</span>
                          <span className="text-xs text-slate-400 shrink-0">{fmtSize(att.size)}</span>
                        </button>
                      ) : (
                        <div className="flex items-center gap-2 min-w-0">
                          <Paperclip className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          <span className="text-sm text-slate-700 truncate">{att.filename}</span>
                          <span className="text-xs text-slate-400 shrink-0">{fmtSize(att.size)}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1 shrink-0">
                        {persisted && (
                          <Button type="button" size="icon" variant="ghost" className="h-7 w-7" data-testid={`quote-attachment-download-${att.id}`} onClick={() => downloadAttachment(editing.id, att)}><Download className="h-3.5 w-3.5 text-slate-500" /></Button>
                        )}
                        <Button type="button" size="icon" variant="ghost" className="h-7 w-7" data-testid={`quote-attachment-remove-${att.id}`} onClick={() => removeAttachment(att)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 rounded-lg border border-slate-200 p-3">
              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="quote-nonbinding-checkbox" className="mt-1 h-4 w-4 rounded border-gray-300" checked={form.is_non_binding} onChange={(e) => setForm({ ...form, is_non_binding: e.target.checked })} />
                  <span className="text-sm">
                    <span className="font-medium text-slate-900">Freibleibend (unverbindlich)</span>
                    <span className="block text-xs text-gray-500">{form.is_non_binding ? "Angebot ist unverbindlich (freibleibend)." : "Angebot ist bis zum Gültig-bis-Datum verbindlich."}</span>
                  </span>
                </label>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Ablauf-Erinnerung senden an</Label>
                <select data-testid="quote-reminder-select" className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={form.reminder_recipients} onChange={(e) => setForm({ ...form, reminder_recipients: e.target.value })}>
                  <option value="self">Nur an mich</option>
                  <option value="both">An mich &amp; Kunde</option>
                </select>
                <span className="block text-xs text-gray-400 mt-1">3 Tage vor Ablauf (Status „Versendet").</span>
              </div>
            </div>

            <div className="rounded-lg bg-slate-50 p-3 text-sm space-y-1" data-testid="quote-totals">
              <div className="flex justify-between"><span className="text-gray-600">Netto</span><span>{EUR(computed.subtotal)}</span></div>
              {!zeroVat && <div className="flex justify-between"><span className="text-gray-600">USt</span><span>{EUR(computed.tax)}</span></div>}
              <div className="flex justify-between font-semibold text-slate-900"><span>Gesamt</span><span>{EUR(computed.total)}</span></div>
            </div>
          </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="quote-save-button">{saving ? "Speichert…" : editing ? "Speichern" : "Angebot erstellen"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialog.open} onOpenChange={(o) => setEmailDialog((d) => ({ ...d, open: o }))}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Angebot versenden</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">Das Angebot {emailDialog.quote?.quote_number} wird als PDF mit Online-Annahme-Link versendet.</p>
            <div><Label>Empfänger-E-Mail</Label><Input data-testid="quote-email-recipient" type="email" value={emailDialog.recipient} onChange={(e) => setEmailDialog((d) => ({ ...d, recipient: e.target.value }))} placeholder="kunde@beispiel.de" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog({ open: false, quote: null, recipient: "", sending: false })}>Abbrechen</Button>
            <Button onClick={sendEmail} disabled={emailDialog.sending} data-testid="quote-email-send-button">{emailDialog.sending ? "Sendet…" : "Senden"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog isOpen={confirmDel.open} onClose={() => setConfirmDel({ open: false, id: null })} onConfirm={doDelete}
        title="Angebot löschen?" description="Diese Aktion kann nicht rückgängig gemacht werden." confirmText="Löschen" cancelText="Abbrechen" variant="destructive" />

      {/* Promote inline-customer dialog (wie im Rechnungsgenerator) */}
      <Dialog open={promoteDialog.open} onOpenChange={(o) => { if (!o) setPromoteDialog({ open: false, customerId: null, customerName: "" }); }}>
        <DialogContent className="max-w-md" data-testid="quote-promote-customer-dialog">
          <DialogHeader><DialogTitle>Kunde in deine Kunden-DB übernehmen?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-700">Du hast „<span className="font-semibold">{promoteDialog.customerName}</span>" gerade direkt im Angebot erfasst. Sollen die Kundendaten dauerhaft in deine Kundenliste übernommen werden?</p>
            <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-800">
              <strong>Tipp:</strong> Bei Übernahme kannst du den Kunden für Folge-Angebote, Rechnungen oder Projekte wiederverwenden.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="quote-promote-skip" onClick={() => setPromoteDialog({ open: false, customerId: null, customerName: "" })}>Nein, nur dieses Angebot</Button>
            <Button data-testid="quote-promote-confirm" onClick={promoteCustomer}>Ja, übernehmen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Erinnerung-Bestätigung */}
      <Dialog open={reminderConfirm.open} onOpenChange={(o) => { if (!o) setReminderConfirm({ open: false, quote: null, sending: false }); }}>
        <DialogContent className="max-w-md" data-testid="quote-reminder-confirm-dialog">
          <DialogHeader><DialogTitle>Erinnerung senden?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-700 py-1">
            Der Kunde {reminderConfirm.quote?.customer_name ? `„${reminderConfirm.quote.customer_name}"` : ""} erhält eine Erinnerung
            zum Angebot <span className="font-medium">{reminderConfirm.quote?.quote_number}</span> – inklusive Angebots-PDF
            {(reminderConfirm.quote?.attachments || []).length > 0 ? " und Anhängen" : ""} sowie dem Online-Annahme-Link.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReminderConfirm({ open: false, quote: null, sending: false })}>Abbrechen</Button>
            <Button onClick={sendReminder} disabled={reminderConfirm.sending} data-testid="quote-reminder-confirm-send">{reminderConfirm.sending ? "Sendet…" : "Erinnerung senden"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* FL: als abgelehnt markieren (mit Begründung) */}
      <Dialog open={declineDialog.open} onOpenChange={(o) => { if (!o) setDeclineDialog({ open: false, quote: null, reason: "", saving: false }); }}>
        <DialogContent className="max-w-md" data-testid="quote-decline-dialog">
          <DialogHeader><DialogTitle>Angebot als abgelehnt markieren?</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-700">Angebot <span className="font-medium">{declineDialog.quote?.quote_number}</span> wird als abgelehnt markiert.</p>
            <div><Label className="text-xs text-gray-500">Begründung (optional)</Label>
              <Textarea data-testid="quote-decline-reason" rows={2} value={declineDialog.reason} onChange={(e) => setDeclineDialog((d) => ({ ...d, reason: e.target.value }))} placeholder="z.B. Budget des Kunden zu gering" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineDialog({ open: false, quote: null, reason: "", saving: false })}>Abbrechen</Button>
            <Button className="bg-red-600 hover:bg-red-700" onClick={submitDecline} disabled={declineDialog.saving} data-testid="quote-decline-confirm">{declineDialog.saving ? "Speichert…" : "Als abgelehnt markieren"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
